import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import cn from 'classnames';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import Flex from '@wix/da-ds/pkg/Flex';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import CancelButton from '@wix/da-ds/pkg/modals/dsPresets/MessageModal/buttons/CancelButton';
import useModalDefaults from '../../../modals/seller/hooks/useModalDefaults';
import ExclusiveCategoryRadioButtons from '../../../modals/seller/form/ExclusiveCategoryRadioButtons';
import AllowResaleToggle from '../../../modals/seller/form/AllowResaleToggle';
import EarlyAccessToggle from '../../../modals/seller/form/EarlyAccessToggle';
import OfferDelayedStartToggle from '../../../modals/seller/form/OfferDelayedStartToggle';
import { FormField } from '../../../modals/seller/redux/types';

import sellerModalStyles from '../../../modals/seller/SingularSellerModal/SingularSellerModal.scss';

export interface SettingsFormFields {
  [FormField.Category]: string;
  [FormField.EarlyAccess]: boolean;
  [FormField.AllowResale]: boolean;
  [FormField.OfferDelayedStart]: boolean;
}

interface Props {
  acceptingOffers?: boolean;
  initialValues: SettingsFormFields;
  isOpen?: boolean;
  onRequestClose: () => void;
  onSave: (data: SettingsFormFields) => void;
}

const AdoptableSettingsModal: React.FC<Props> = ({
  acceptingOffers,
  initialValues,
  isOpen,
  onRequestClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const modalDefaults = useModalDefaults();
  const formContext = useForm<SettingsFormFields>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: initialValues,
  });

  const onSubmit = useCallback(() => {
    formContext.handleSubmit(onSave)();
    onRequestClose();
  }, [formContext, onSave, onRequestClose]);

  return (
    <Modal
      {...modalDefaults}
      className={cn(sellerModalStyles['modal'], sellerModalStyles['specificify-booster'])}
      contentClassName={sellerModalStyles['content']}
      heading={t('sellerModal.adoptableSettings.heading')}
      footerRight={[
        <CancelButton key="cancel" onClick={onRequestClose}>
          {t('popup.cancel')}
        </CancelButton>,
        <BrandedButton key="save" onClick={onSubmit} size="large" variant="brand-green">
          {t('popup.confirm')}
        </BrandedButton>,
      ]}
      isOpen={isOpen ?? false}
      onRequestClose={onRequestClose}
      bodyScrollLockOptions={{
        allowTouchMove: (el: HTMLElement) => {
          // Allow the slider to scroll horizontally
          return el.closest('.exclusive-category-slider') !== null;
        },
      }}
    >
      <FormProvider {...formContext}>
        <Flex direction="column" gap={24}>
          <ExclusiveCategoryRadioButtons sliderContentClassName={'exclusive-category-slider'} />
          <EarlyAccessToggle />
          <AllowResaleToggle />
          <OfferDelayedStartToggle
            disabled={!acceptingOffers}
            subLabel={t(
              'deviationSubmit.form.sellExclusive.advancedSettings.offerDelayedStart.subLabel'
            )}
          />
        </Flex>
      </FormProvider>
    </Modal>
  );
};
AdoptableSettingsModal.displayName = 'AdoptableSettingsModal';

export default AdoptableSettingsModal;
